import { IBookableDatesInfo } from "@/domain/entities/book";
import { IBookRepository } from "@/repositories/book/bookRepository.types";
import { BookIntervalCode, ErrorTypeCode } from "@/types";
import { BookableDatesUseCase } from "./interface/BookableDatesUseCase";
import { IResultData } from "@/domain/entities/shared/result";
import { CustomError } from "@/repositories/shared/errors/customError";
import useSetResultCodeIfNeeded from "@/use/useSetResultCodeIfNeeded";
import { IsDailyBookCheaperUseCase } from "./IsDailyBookCheaper";

export class GetMonthlyBookableDates implements BookableDatesUseCase {
  private bookRepository: IBookRepository;
  private dailyFare: number;
  private monthlyFare: number;
  private isDailyBookCheaper: IsDailyBookCheaperUseCase;

  constructor(
    bookRepository: IBookRepository,
    dailyFare: number,
    monthlyFare: number,
    isDailyBookCheaper: IsDailyBookCheaperUseCase
  ) {
    this.bookRepository = bookRepository;
    this.dailyFare = dailyFare;
    this.monthlyFare = monthlyFare;
    this.isDailyBookCheaper = isDailyBookCheaper;
  }

  async execute(
    turnId: string,
    getOnBusStopId: string,
    getOffBusStopId: string,
    fetchLatest?: boolean,
    targetSeatNo?: string
  ): Promise<IResultData<IBookableDatesInfo[]>> {
    const bookableDatesResult: IResultData<IBookableDatesInfo[]> = {
      isSuccess: false,
      data: [],
    };

    const result = await this.bookRepository.getBookableDatesInfos(
      turnId,
      getOnBusStopId,
      getOffBusStopId,
      !!fetchLatest,
      BookIntervalCode.MONTH,
      targetSeatNo
    );

    try {
      result
        .map((bookableDatesInfos) => {
          bookableDatesResult.isSuccess = true;
          bookableDatesResult.data =
            this.handleMonthlyBookableDates(bookableDatesInfos);
        })
        .mapErr((error) => {
          useSetResultCodeIfNeeded(bookableDatesResult, error);
          bookableDatesResult.errorMessage = error.message;
        });
    } catch (error: any) {
      if (!(error instanceof CustomError)) return bookableDatesResult;

      bookableDatesResult.isSuccess = false;
      bookableDatesResult.errorType = error.type;
      bookableDatesResult.errorMessage = "월 예약 불가";
    }

    return bookableDatesResult;
  }

  private handleMonthlyBookableDates(bookableDatesInfos: IBookableDatesInfo[]) {
    if (!bookableDatesInfos.length)
      throw new CustomError(ErrorTypeCode.EMPTY_MONTHLY_BOOKABLE_DATES);

    const [thisMonthInfo, nextMonthInfo] = bookableDatesInfos;

    // 이번달 월예약이 일예약 금액과 같거나 더 저렴한 경우 이번달 반환
    if (
      !this.isDailyBookCheaper.execute(
        this.dailyFare,
        this.monthlyFare,
        thisMonthInfo
      )
    ) {
      return [thisMonthInfo];
    }

    // 다음달이 없거나 다음달 월예약이 일예약보다 더 비싼 경우 월예약 불가 에러 처리
    if (
      !nextMonthInfo ||
      this.isDailyBookCheaper.execute(
        this.dailyFare,
        this.monthlyFare,
        nextMonthInfo
      )
    ) {
      throw new CustomError(ErrorTypeCode.MONTHLY_BOOK_UNAVAILABLE);
    } else {
      return [nextMonthInfo];
    }
  }
}
